import { defineStore } from 'pinia'
import { api, ApiError, setOnUnauthorized } from '@/lib/api'

/**
 * ADR-008 RBAC roles — additive (viewer ⊂ editor ⊂ admin). Server is
 * the source of truth; these constants exist so component templates
 * can ask the auth store "am I at least an editor?" instead of
 * stringly comparing.
 */
export const ROLES = ['viewer', 'editor', 'admin'] as const
export type Role = (typeof ROLES)[number]

const ROLE_RANK: Record<Role, number> = { viewer: 1, editor: 2, admin: 3 }

/** Server-side /auth/me response (mirrors admin-server handler.MeResponse). */
export interface Me {
  id: string
  username: string
  /** ADR-008 role. Older server builds may omit this; treat as
   *  empty string and let `roleAtLeast` fail-closed (deny everything
   *  above viewer floor) until /auth/me confirms otherwise. */
  role: Role | ''
  mustChangePassword: boolean
  passwordExpiresAt?: string
  lastLoginAt?: string
}

/** Login response (mirrors handler.LoginResponse). */
interface LoginResponse {
  mustChangePassword: boolean
}

type AuthPhase = 'idle' | 'loading' | 'authenticated' | 'anonymous'

/**
 * Single source of truth for "who is logged in". The Pinia store
 * is the ONLY thing the router guard reads — components subscribe via
 * the store's selectors and don't poke at /auth/me directly.
 *
 * Phase machine:
 *   idle → loading → (authenticated | anonymous)
 *     bootstrap()              login()                logout()
 *
 * Re-entrant: a parallel pair of login() + bootstrap() (e.g. tab
 * focus + click) sees the second call resolve against the same
 * inflight promise.
 */
export const useAuthStore = defineStore('auth', {
  state: () => ({
    phase: 'idle' as AuthPhase,
    me: null as Me | null,
    /** Inflight bootstrap promise, used to deduplicate parallel calls. */
    inflight: null as Promise<void> | null,
  }),
  getters: {
    isAuthenticated: (state) => state.phase === 'authenticated' && state.me !== null,
    mustChangePassword: (state) => state.me?.mustChangePassword ?? false,
    /**
     * Current role, narrowed to a known Role. `null` covers:
     *   - not yet authenticated
     *   - server returned a role we don't recognise
     *   - older server build that omits the field
     * Treat null as "less than viewer" so callers fail-closed.
     */
    role: (state): Role | null => {
      const r = state.me?.role
      return r && (r as Role) in ROLE_RANK ? (r as Role) : null
    },
  },
  actions: {
    /**
     * Called once at app boot (and re-triggered after login). Sets the
     * auth phase based on whether the cookie still resolves to a user.
     *
     * Three cases the router guard relies on:
     *   1. inflight non-null → another caller already kicked off
     *      fetchMe; await the SAME promise so we don't double-fire.
     *      This is what fixes the App.vue-mounted vs guard race —
     *      whichever fires first sets inflight, the other piggybacks.
     *   2. phase already terminal (authenticated / anonymous) → we've
     *      done this dance already; just return so a re-call from a
     *      later navigation doesn't knock the store back to 'loading'.
     *   3. idle → kick off fetchMe, await it, terminate phase.
     */
    async bootstrap(): Promise<void> {
      if (this.inflight) return this.inflight
      if (this.phase === 'authenticated' || this.phase === 'anonymous') return
      this.phase = 'loading'
      this.inflight = this.fetchMe()
      try {
        await this.inflight
      } finally {
        this.inflight = null
      }
    },
    async fetchMe(): Promise<void> {
      try {
        const me = await api.get<Me>('/api/admin/v1/auth/me')
        this.me = me
        this.phase = 'authenticated'
      } catch (err) {
        // 401 (no/bad cookie) and any network failure both land us
        // in the anonymous state. The router guard sends us to /login.
        if (err instanceof ApiError && err.status === 401) {
          this.me = null
          this.phase = 'anonymous'
          return
        }
        this.me = null
        this.phase = 'anonymous'
        // Re-throw non-401 errors so the LoginPage can surface them.
        throw err
      }
    },
    async login(username: string, password: string): Promise<void> {
      const res = await api.post<LoginResponse>('/api/admin/v1/auth/login', {
        body: { username, password },
      })
      // Refresh me so consumers see mustChangePassword + lastLoginAt.
      await this.fetchMe()
      // Belt and braces: the response already tells us, but /me is
      // the canonical state. If they disagree, /me wins.
      if (this.me && res.mustChangePassword) {
        this.me.mustChangePassword = true
      }
    },
    async logout(): Promise<void> {
      try {
        await api.post('/api/admin/v1/auth/logout')
      } catch (err) {
        // Logout failures are rare and not user-actionable. Drop the
        // local state regardless so the UI doesn't pretend they're
        // still logged in.
        if (!(err instanceof ApiError)) throw err
      }
      this.me = null
      this.phase = 'anonymous'
    },
    async changePassword(oldPassword: string, newPassword: string): Promise<void> {
      await api.post('/api/admin/v1/auth/change-password', {
        body: { oldPassword, newPassword },
      })
      // Server returns 204 + new cookie; refresh /me so
      // mustChangePassword + passwordExpiresAt update.
      await this.fetchMe()
    },
    /**
     * Predicate the rest of the SPA reads to decide whether a
     * destructive button renders enabled vs disabled-with-tooltip.
     * Mirrors model.Role.AtLeast on the server — keep the rank ordering
     * in sync if a fourth role lands.
     */
    roleAtLeast(min: Role): boolean {
      const have = this.role
      if (have === null) return false
      return ROLE_RANK[have] >= ROLE_RANK[min]
    },
  },
})

/**
 * Wire api.ts's 401 hook to the Pinia store. Called once at module
 * load by `installAuthHooks()` in main.ts (avoids importing the store
 * inside api.ts, which would create a circular dep and tank tree-shake).
 *
 * Marked exported separately from the store so tests can opt out.
 */
export function installAuthHooks(): void {
  setOnUnauthorized(() => {
    const store = useAuthStore()
    store.$patch({ me: null, phase: 'anonymous' })
  })
}
